import Sugar from 'sugar';
import { html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { customElement, query, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { consume } from '@lit/context';
import { ltnToastServiceContext, nsSettingsServiceContext } from '../../../ns-services/contexts.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';
import { LtnToastService } from '../../../ltn-toast/ltn-toast-service.js';

import { NSSettingsService } from '../../../ns-settings/ns-settings-service.js';

import { FinalFormController, FormConfig } from '../../../ns-form/final-form-controller.js';

import JobSchema from '../../../models/job-schema.js';
import Invitation from '../../../models/invitation.js';

import { callLambda } from '../../../helpers/lambda.js';

import NSJobComponent from '../job.js';

import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/invite-user.json';
import { jobSchema } from '../job-schema.js';
import {
  InviteFormValues as FormValues,
  invitationMetadata,
  inviteFormErrors,
  invitesPaymentApprover,
  paymentApproverKnown,
} from './invite-user-logic.js';

interface Invitee {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
}

@customElement(NSInviteUser.is)
export default class NSInviteUser extends NSJobComponent {
  static get is() {
    return 'ns-invite-user';
  }

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  private __formController: FinalFormController<FormValues>;

  @state()
  private __addPaymentApproverForm: boolean = false;

  @query('wa-checkbox')
  private __checkbox?: WaCheckbox;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { invited: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      /* The form is the layout's last element, so its last panel runs to the column's foot */
      form {
        display: flex;
        flex-direction: column;
      }

      form > .job-panel:last-child {
        flex: 1 0 auto;
      }
    `,
  ];

  constructor() {
    super();

    this.__formController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__formSubmit(values),
      validate: (values) => inviteFormErrors(values, this.__invitesPaymentApprover),
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__settings) throw new Error('Unable to find settings service');

    if (!this._directory) throw new Error('Unable to find NSWorkflowService');

    if (!this.__toast) throw new Error('Unable to find toast service');
  }

  // The workflow already has its payment approver (the first user, who claimed it): the card invites only a director
  private get __paymentApproverKnown() {
    return paymentApproverKnown(this._job?.inputValues);
  }

  private get __invitesPaymentApprover() {
    return invitesPaymentApprover(this.__paymentApproverKnown, Boolean(this.__checkbox?.checked));
  }

  private __samePaymentApproverChanged(ev: Event) {
    const isChecked = (ev.target as WaCheckbox).checked;

    this.__formController.form.change('sameUser', isChecked);
    this.__addPaymentApproverForm = !isChecked;
  }

  private async __formSubmit(value: Partial<FormValues>) {
    const directorFirstName = value.directorFirstName;
    if (!directorFirstName) throw new Error('Unable to create invite without a director first name');
    const directorLastName = value.directorLastName;
    if (!directorLastName) throw new Error('Unable to create invite without a director last name');
    const directorJobTitle = value.directorJobTitle;
    if (!directorJobTitle) throw new Error('Unable to create invite without a director job title');
    const directorEmail = value.directorEmail;
    if (!directorEmail) throw new Error('Unable to create invite without a director email');

    const invitees: Invitee[] = [
      { firstName: directorFirstName, lastName: directorLastName, jobTitle: directorJobTitle, email: directorEmail },
    ];
    if (this.__invitesPaymentApprover) {
      const paymentApproverFirstName = value.paymentApproverFirstName;
      if (!paymentApproverFirstName) throw new Error('Unable to create invite without a payment approver first name');
      const paymentApproverLastName = value.paymentApproverLastName;
      if (!paymentApproverLastName) throw new Error('Unable to create invite without a payment approver last name');
      const paymentApproverJobTitle = value.paymentApproverJobTitle;
      if (!paymentApproverJobTitle) throw new Error('Unable to create invite without a payment approver job title');
      const paymentApproverEmail = value.paymentApproverEmail;
      if (!paymentApproverEmail) throw new Error('Unable to create invite without a payment approver email');

      invitees.push({
        firstName: paymentApproverFirstName,
        lastName: paymentApproverLastName,
        jobTitle: paymentApproverJobTitle,
        email: paymentApproverEmail,
      });
    }

    this.loading(invitees.length > 1 ? 'Inviting Director and Payment Approver' : 'Inviting Director');
    try {
      const [directorInvitation, paymentApproverInvitation] = await this.__createInvitations(invitees);
      if (!directorInvitation) throw new Error('Unable to invite the director.');
      const metadata = invitationMetadata(
        this.__paymentApproverKnown,
        directorInvitation.id,
        paymentApproverInvitation?.id ?? null,
      );
      for (const [key, id] of metadata) await this.updateMetadata(key, id);

      await this.outcome('invited');

      this.__toast?.queueToast({
        label: 'Invitation(s) have been created and email(s) will be sent out shortly',
        timeout: 4000,
      });
    } catch (err) {
      this.showError(err, 'Unable to send the invitations.');
    } finally {
      this.loaded();
    }
  }

  // One call for everyone, so a refusal leaves no one invited; the invitations come back in the order asked for
  private async __createInvitations(invitees: Invitee[]): Promise<(Invitation | undefined)[]> {
    if (!this._db) throw new Error('Missing required db service');
    if (!this._auth) throw new Error('Missing required auth service');
    if (!this._directory) throw new Error('Missing required directory service');
    if (!this._directory.me) throw new Error('Unable to find user profile');
    if (!this._directory.company) throw new Error('Unable to find active company');
    if (!this._job) throw new Error('Missing required job');
    const profile = this._directory.me;
    const company = this._directory.company;

    const inviterEmail = profile.email;
    if (!inviterEmail) throw new Error('Unable to find current users email to send invite.');

    const invitations: Invitation[] = [];
    for (const { firstName, lastName, jobTitle, email } of invitees) {
      const invitation = this._db.createObject<Invitation>('invitation');

      invitation.inviter.personId = profile.id;
      invitation.inviter.email = inviterEmail;
      invitation.inviter.companyId = company.id;

      invitation.invitee.name = `${firstName} ${lastName}`;
      invitation.invitee.jobTitle = jobTitle;
      invitation.invitee.email = email;
      invitation.invitee.company.id = company.id;
      invitation.invitee.company.name = company.name;

      invitation.expiryDate = Sugar.Date.create('in 1 day');

      const testRes = await this._db.query('invitation', {
        'inviter.email': {
          $eq: invitation.inviter.email,
        },
        'invitee.email': {
          $eq: invitation.invitee.email,
        },
      });

      if (testRes.results.length > 0) {
        throw new Error(`An invitation has already been sent to ${email}.`);
      }
      invitations.push(invitation);
    }

    const output = await callLambda(
      'onboarding/create-invitation',
      'POST',
      this._auth.token,
      JSON.stringify({
        userId: profile.userId,
        personId: profile.id,
        oauthApp: sessionStorage.getItem('oauthApp'),
        companyId: company.id,
        // This open card lets its assignee invite into their own company before they hold PEOPLE_INVITE
        jobId: this._job.id,
        invitations,
      }),
    );
    const created = (output.res as unknown as { invitations?: Invitation[] }).invitations ?? [];
    return invitees.map(({ email }) => created.find((invitation) => invitation.invitee.email === email));
  }

  private __renderField(register: Function, field: keyof FormValues, label: string) {
    return html`
      <div class="layout vertical">
        <ns-input-field stacked required ${register(field)} placeholder=${label} label="${label}*:"></ns-input-field>
        ${this.__renderErrorSpan(field)}
      </div>
    `;
  }

  private __paymentApproverInputFields(register: Function) {
    return html`
      <div class="job-panel">
        <h3>Payment Approver's Details</h3>
        ${this.__renderField(register, 'paymentApproverFirstName', 'First Name')}
        ${this.__renderField(register, 'paymentApproverLastName', 'Last Name')}
        ${this.__renderField(register, 'paymentApproverJobTitle', 'Job Title')}
        ${this.__renderField(register, 'paymentApproverEmail', 'Email')}
      </div>
    `;
  }

  render() {
    const { form, register } = this.__formController;
    const known = this.__paymentApproverKnown;

    return html`
      <ns-job-layout>
        <p slot="intro">
          ${known
            ? 'You are the payment approver for the company. Enter the details of one of its Directors to send an invitation to.'
            : 'Enter the details of one of the Directors of the company to send a invitation too.'}
        </p>
        <form
          @submit=${(e: CustomEvent) => {
            e.preventDefault();
            form.submit();
          }}
        >
          <div class="job-panel">
            <h3>Director's Details</h3>
            ${this.__renderField(register, 'directorFirstName', 'First Name')}
            ${this.__renderField(register, 'directorLastName', 'Last Name')}
            ${this.__renderField(register, 'directorJobTitle', 'Job Title')}
            ${this.__renderField(register, 'directorEmail', 'Email')}
            ${when(
              !known,
              () => html`
                <wa-checkbox checked @change=${this.__samePaymentApproverChanged}>
                  Same user can make payments on behalf of the company.
                </wa-checkbox>
              `,
            )}
          </div>
          ${when(!known && this.__addPaymentApproverForm, () => this.__paymentApproverInputFields(register))}
        </form>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const { form } = this.__formController;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${form.getState().submitting} @click=${() => form.submit()}>
        Complete
      </wa-button>
    `;
  }

  private __renderErrorSpan(field: keyof FormValues) {
    const { touched, error } = this.__formController.form.getFieldState(field) ?? {};
    return html`
      <span class="error-text" style=${styleMap({ display: touched && error ? 'block' : 'none' })}>${error}</span>
    `;
  }
}
